import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import { osirisTokens } from '@/theme/tokens';
import { Icon } from './Icon';

export type MetricTone = 'default' | 'success' | 'warning' | 'error';

interface MetricTileProps {
  label?: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  tone?: MetricTone;
  /** Icon-led tile (Figma 29262:218588): 20px glyph left of the value, no text label. */
  icon?: string;
  className?: string;
  sx?: SxProps<Theme>;
}

const TONE_COLOR: Record<MetricTone, string> = {
  default: osirisTokens.text.primary,
  success: osirisTokens.text.success,
  warning: osirisTokens.text.warning,
  error: osirisTokens.text.error,
};

export function MetricTile({
  label,
  value,
  unit,
  tone = 'default',
  icon,
  className,
  sx,
}: MetricTileProps) {
  const data = (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: 34, minWidth: 0 }}>
      <Typography
        className="metric-tile-value"
        sx={{
          fontFamily: osirisTokens.fonts.display,
          fontSize: unit ? 26 : 20,
          fontWeight: 400,
          lineHeight: 1,
          color: TONE_COLOR[tone],
          letterSpacing: '0.01em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
      {unit && (
        <Typography
          className="metric-tile-unit"
          sx={{
            fontFamily: osirisTokens.fonts.body,
            fontSize: 20,
            fontWeight: 400,
            lineHeight: 1,
            pb: '2px',
            color: TONE_COLOR[tone],
            letterSpacing: '0.01em',
            textTransform: 'lowercase',
            whiteSpace: 'nowrap',
          }}
        >
          {unit}
        </Typography>
      )}
    </Box>
  );

  if (icon) {
    return (
      <Box
        className={`metric-tile metric-tile-icon${className ? ' ' + className : ''}`}
        aria-label={typeof label === 'string' ? label : undefined}
        sx={[
          {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: 56,
            px: '10px',
            py: '8px',
            borderTop: `1px solid ${osirisTokens.border.primary}`,
            borderLeft: `1px solid ${osirisTokens.border.primary}`,
            minWidth: 0,
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        <Icon name={icon} size={20} sx={{ color: osirisTokens.icons.muted }} />
        {data}
      </Box>
    );
  }

  return (
    <Box
      className={`metric-tile${className ? ' ' + className : ''}`}
      sx={[
        {
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          px: '12px',
          py: '8px',
          bgcolor: osirisTokens.bg.surface,
          borderTop: `1px solid ${osirisTokens.border.primary}`,
          borderLeft: `1px solid ${osirisTokens.border.primary}`,
          minWidth: 0,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Typography
        className="metric-tile-label"
        sx={{
          fontFamily: osirisTokens.fonts.body,
          fontSize: 14,
          fontWeight: 400,
          letterSpacing: '0.01em',
          textTransform: 'capitalize',
          color: osirisTokens.text.muted,
          lineHeight: 1.4,
        }}
      >
        {label}
      </Typography>
      {data}
    </Box>
  );
}
